<template>
  <global-modal name="service-modal" ref="modal" @before-open="beforeOpen">
    <Modal @close="$modal.hide('service-modal')">
      <section class="service-modal">
        <span v-if="loading" class="page-loading">
          {{ $t("services.loading") }}
        </span>
        <div class="page-loading" v-else-if="is403Error">
          {{ $t("services.error403") }}
        </div>
        <template v-else-if="service">
          <h1 class="service-modal__title">
            {{ service | localeField("name") }}
          </h1>
          <div class="service-modal__body">
            <ServiceImagesSlider
              :images="service.images"
              class="service-modal__slider"
              full-width
            />
            <div
              class="service-modal__description"
              v-if="description"
              v-html="description"
            ></div>
            <ServiceListTableComponent :rows="[service]" is-service-modal />
            <div
              v-if="!(service.prices && service.prices.length)"
              class="service-modal__warning"
            >
              <icon name="information" />
              <span> {{ $t("services.serviceModal.noPriceNotice") }} </span>
            </div>
            <div class="service-modal__bottom">
              <div class="service-modal__sum">
                <span class="service-modal__sum-title">
                  {{ $t("services.serviceModal.servicesSum") }}:
                </span>
                <span class="service-modal__sum-value">
                  {{ cartSum(service) | formatPrice }}
                </span>
                <span
                  class="service-modal__sum-value service-modal__sum-currensy"
                >
                  {{ currency.symbol }}
                </span>
                <EventCurrencyDropdown
                  v-if="priceTypes && priceTypes.length > 1"
                  :value="currentCurrencyType"
                  :options="priceTypes"
                  @input="setCurrentCurrencyTypeHandler"
                />
              </div>
              <div class="service-modal__actions">
                <button
                  @click="$modal.hide('service-modal')"
                  class="service-modal__back button-clean"
                >
                  <icon name="arrow-left" />
                  <span>{{ $t("services.serviceModal.backBtn") }}</span>
                </button>
              </div>
            </div>
          </div>
        </template>
      </section>
    </Modal>
  </global-modal>
</template>

<script>
import Modal from "@/components/Modal/Modal.vue";
import ServiceImagesSlider from "@/components/Custom/ServiceImagesSlider.vue";
import ServiceListTableComponent from "@/components/UI/Table/ServiceListTableComponent.vue";
import EventCurrencyDropdown from "@/components/Custom/EventCurrencyDropdown.vue";
import apiServices from "@/api/services";
import { mapGetters, mapState } from "vuex";

export default {
  name: "ServiceModal",
  components: {
    ServiceListTableComponent,
    ServiceImagesSlider,
    Modal,
    EventCurrencyDropdown
  },
  data() {
    return {
      loading: false,
      service: {},
      is403Error: false
    };
  },
  methods: {
    async beforeOpen(event) {
      try {
        this.loading = true;
        if (event.params.id) {
          const { data } = await apiServices.get(
            this.$route.params.id,
            event.params.id
          );
          this.service = data;
        }
      } catch (e) {
        this.is403Error = e.error_data?.response?.status === 403;
      } finally {
        this.loading = false;
      }
    },
    setCurrentCurrencyTypeHandler(type) {
      this.$store.dispatch("event/setCurrency", type.code);
    }
  },
  computed: {
    ...mapGetters("services", ["cartSum", "currency"]),
    ...mapGetters("event", ["priceTypes"]),
    ...mapState("eventCatalog", ["currentCurrencyType"]),

    description() {
      return this.$options.filters.localeField(this.service, "description");
    }
  }
};
</script>

<style lang="scss">
.service-modal {
  display: flex;
  flex-direction: column;
  height: 100%;

  * {
    box-sizing: border-box;
  }
  & br {
    margin: 5px 0;
    content: "";
    display: block;
  }
  & &__title {
    padding: 30px 50px;
    font-size: 2.125rem;
    font-style: normal;
    color: var(--dark);
    font-weight: 700;
    line-height: normal;
    margin: 0;
    width: 100%;
    max-width: 940px;
    @media (max-width: 740px) {
      font-size: 1.375rem;
      font-style: normal;
      font-weight: 700;
      padding: 30px 60px 30px 30px;
      display: block;
      max-height: 56rem;
    }
  }

  &__body {
    width: 100%;
    max-width: 940px;
    padding: 30px 50px 50px;
    display: flex;
    flex-direction: column;
    @media (max-width: 740px) {
      padding: 30px;
    }

    > *:not(:last-child) {
      margin-bottom: 30px;
    }
  }

  &__slider {
    img {
      border: none;
    }
  }

  &__description {
    color: var(--dark-light, #6d7179);
    font-feature-settings: "clig" off, "liga" off;
    font-size: 0.875rem;
    line-height: 22px;
  }

  &__warning {
    padding: 15px 20px;
    display: flex;
    align-items: center;
    border-radius: 2px;
    background: rgba(246, 147, 29, 0.1);

    color: var(--orange, #f6931d);
    font-feature-settings: "clig" off, "liga" off;
    font-size: 0.875rem;
    line-height: 22px;

    svg {
      margin-right: 15px;
      width: 20px;
      height: 20px;
      color: var(--orange, #f6931d);
    }
  }

  &__bottom {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    @media (max-width: 440px) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__sum {
    display: flex;
    align-items: baseline;

    &-title {
      color: var(--dark-light, #6d7179);
      font-feature-settings: "clig" off, "liga" off;
      font-size: 1rem;
      font-weight: 500;
      line-height: 24px;
      margin-right: 10px;
      flex-shrink: 0;
      @media (max-width: 440px) {
        margin-bottom: 15px;
      }
    }

    &-value {
      font-feature-settings: "clig" off, "liga" off;
      font-size: 1.375rem;
      font-weight: 700;
      line-height: 28px;
    }
    &-currensy {
      display: inline-block;
      margin-left: 5px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    @media (max-width: 420px) {
      flex-direction: column-reverse;
      align-items: flex-start;
    }
  }

  &__back {
    display: flex;
    align-items: center;
    color: var(--main, #009ee3);
    font-feature-settings: "clig" off, "liga" off;
    font-size: 0.875rem;
    line-height: 22px;
    padding: 10px;
    border-radius: 2px;
    @media (max-width: 420px) {
      width: 100%;
      margin: 0;
      background: var(--gray-2);
      justify-content: center;
    }

    &:hover {
      background: var(--gray);
    }

    svg {
      width: 20px;
      height: 20px;
      margin-right: 5px;
    }
  }

  &__cart-btn {
    @media (max-width: 420px) {
      width: 100%;
      margin-bottom: 10px;
    }
  }
}
</style>
